import { trans } from 'sharedKernel/i18n'
import { formatDateForApi, isDateBeforeToday } from 'sharedKernel/handlers'
import { hasVisibleExistingCover } from './opportunityFileSync'
import { OPPORTUNITY_VERTICAL_OPTIONS, OPPORTUNITY_VERTICAL_OTHER_ID } from './constants'
import { OpportunityFormData } from './types'

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'
const SCOUTING_FUNNEL_TITLE = 'Scouting Funnel'

export interface OpportunityFilePayload {
  Content: string
  Mimetype: string
  Name: string
  Type: string
}

const stripHtml = (value: string): string =>
  value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()

const parseMaximumOpportunityValue = (value: string): number | null => {
  const raw = value.trim()
  if (!raw) return null

  const cleaned = raw.replace(/\s/g, '').replace(/R\$/gi, '')
  let normalized = cleaned

  if (cleaned.includes(',') && cleaned.includes('.')) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.')
  } else if (cleaned.includes(',')) {
    normalized = cleaned.replace(',', '.')
  }

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

const buildVerticals = (form: OpportunityFormData): string[] =>
  OPPORTUNITY_VERTICAL_OPTIONS
    .filter(option => form.verticalSectors[option.id])
    .map(option => (option.id === OPPORTUNITY_VERTICAL_OTHER_ID ? 'Other' : option.id))

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
  })

const buildFilePayload = async (file: File, type: string): Promise<OpportunityFilePayload> => {
  const content = await fileToBase64(file)
  return {
    Content: content.split(',')[1] ?? '',
    Mimetype: file.type,
    Name: file.name,
    Type: type,
  }
}

export const buildOpportunityFiles = async (
  form: OpportunityFormData
): Promise<OpportunityFilePayload[]> => {
  const files: OpportunityFilePayload[] = []

  if (form.coverImage) {
    files.push(await buildFilePayload(form.coverImage, 'coverImage'))
  }

  for (const file of form.supportMaterials) {
    files.push(await buildFilePayload(file, 'supportFiles'))
  }

  return files
}

export const buildOpportunityPayload = (
  form: OpportunityFormData,
  statusId?: string,
  opportunityId?: string,
  files?: OpportunityFilePayload[]
): Record<string, unknown> => {
  const institutionName = form.offeringInstitutionName.trim()
  const otherVertical = form.verticalSectors[OPPORTUNITY_VERTICAL_OTHER_ID]
    ? form.verticalOther.trim()
    : undefined

  return {
    data: {
      ...(opportunityId ? { id: opportunityId } : {}),
      statusId: statusId || EMPTY_GUID,
      title: form.opportunityTitle.trim(),
      description: form.description.trim(),
      requesterName: institutionName,
      institutionName,
      operationArea: '',
      verticals: buildVerticals(form),
      ...(otherVertical ? { otherVertical } : {}),
      interestDeadline: formatDateForApi(form.interestDeadline),
      maximumOpportunityValue: parseMaximumOpportunityValue(form.maxValue),
      impactedAudience: form.impactedBy.trim(),
      references: form.references.trim(),
      supportLink: form.supportLink.trim(),
      ...(opportunityId
        ? {}
        : {
            scouting: [
              {
                title: SCOUTING_FUNNEL_TITLE,
                stages: [],
                criteria: [],
              },
            ],
          }),
      ...(files && files.length > 0 ? { files } : {}),
    },
  }
}

export const validateOpportunityForm = (
  form: OpportunityFormData
): { isValid: boolean; message: string } => {
  if (!form.offeringInstitutionName.trim()) {
    return {
      isValid: false,
      message: trans('scouting.opportunity.validation.offeringInstitutionNameRequired'),
    }
  }

  if (!form.interestDeadline.trim()) {
    return {
      isValid: false,
      message: trans('scouting.opportunity.validation.interestDeadlineRequired'),
    }
  }

  if (isDateBeforeToday(form.interestDeadline)) {
    return {
      isValid: false,
      message: trans('scouting.opportunity.validation.interestDeadlineBeforeToday'),
    }
  }

  if (!form.opportunityTitle.trim()) {
    return {
      isValid: false,
      message: trans('scouting.opportunity.validation.opportunityTitleRequired'),
    }
  }

  if (!stripHtml(form.description)) {
    return {
      isValid: false,
      message: trans('scouting.opportunity.validation.descriptionRequired'),
    }
  }

  const hasVertical = OPPORTUNITY_VERTICAL_OPTIONS.some(option => form.verticalSectors[option.id])
  if (!hasVertical) {
    return {
      isValid: false,
      message: trans('scouting.opportunity.validation.verticalSectorRequired'),
    }
  }

  if (form.verticalSectors[OPPORTUNITY_VERTICAL_OTHER_ID] && !form.verticalOther.trim()) {
    return {
      isValid: false,
      message: trans('scouting.opportunity.validation.verticalOtherRequired'),
    }
  }

  if (!form.impactedBy.trim()) {
    return {
      isValid: false,
      message: trans('scouting.opportunity.validation.impactedByRequired'),
    }
  }

  if (!form.coverImage && !hasVisibleExistingCover(form)) {
    return {
      isValid: false,
      message: trans('scouting.opportunity.validation.coverImageRequired'),
    }
  }

  return { isValid: true, message: '' }
}

export const isOpportunityFormValid = (form: OpportunityFormData): boolean =>
  validateOpportunityForm(form).isValid
